import {
  CdkVirtualScrollViewport,
  VirtualScrollStrategy,
  VIRTUAL_SCROLL_STRATEGY,
} from '@angular/cdk/scrolling';
import {
  ChangeDetectorRef,
  Directive,
  ElementRef,
  forwardRef,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { PlatformService } from '@panamax/app-state';
import {
  clamp,
  CustomSizeVirtualScrollDirective,
  CustomVirtualScrollStrategy,
  ItemHeight,
  Range,
  last,
  intersects,
} from '../custom-virtual-scroll';
import {
  InventoryItem,
  InventoryProductViewModel,
} from '@inventory/models/inventory-view.model';
import { Observable, Subject } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { ListRange } from '@angular/cdk/collections';
import { ItemTypes } from '../../../../lists/shared/list-detail-management/model/list-detail-management-view.model';
import { HotKeys } from '../../../constants/hot-key.enum';

export class InventoryVirtualScrollStrategy implements VirtualScrollStrategy {
  constructor(
    private itemHeights: ItemHeight,
    private indexOffset: number,
    private bufferBefore: number,
    private bufferAfter: number,
    private items: InventoryItem[],
    private platformService: PlatformService,
    private isEditMode: boolean,
    private inventoryId: string,
  ) {}
  private viewport?: CdkVirtualScrollViewport;
  private scrolledIndexChange$ = new Subject<number>();
  public scrolledIndexChange: Observable<number> = this.scrolledIndexChange$.pipe(
    distinctUntilChanged(),
  );
  attach(viewport: CdkVirtualScrollViewport) {
    this.viewport = viewport;
    this.updateTotalContentSize();
    this.updateRenderedRange();
  }
  detach() {
    this.scrolledIndexChange$.complete();
    delete this.viewport;
  }
  public updateItemHeights(
    itemHeights: ItemHeight,
    indexOffset: number,
    bufferBefore: number,
    bufferAfter: number,
    items: any[],
    isEditMode: boolean,
    inventoryId: string,
  ) {
    this.itemHeights = itemHeights;
    this.indexOffset = indexOffset;
    this.bufferBefore = bufferBefore;
    this.bufferAfter = bufferAfter;
    this.items = items;
    this.isEditMode = isEditMode;
    this.inventoryId = inventoryId;
    this.updateTotalContentSize();
    this.updateRenderedRange();
    if (this.viewport) {
      const viewportSize = this.viewport.measureViewportSize('vertical');
      let scrollOffset = this.viewport.measureScrollOffset();
      const itemsInRange = this.calculateItemsInRange(
        scrollOffset,
        viewportSize,
      );
      this.checkDataValuesMatchInputValues(itemsInRange);
    }
  }
  private getItemOffset(index: number): number {
    return this.itemHeights
      ?.slice(this.indexOffset, index + this.indexOffset)
      .reduce((acc, itemHeight) => acc + itemHeight, 0);
  }
  private getTotalContentSize(): number {
    return this.itemHeights?.reduce((a, b) => a + b, 0);
  }
  private getListRangeAt(itemsInRange: number[]): ListRange {
    let start = clamp(
      0,
      (itemsInRange ? itemsInRange[0] : 0 ?? 0) - this.bufferBefore,
      this.itemHeights?.length - 1,
    );
    let end = clamp(
      0,
      (itemsInRange ? last(itemsInRange) : 0 ?? 0) + this.bufferAfter,
      this.itemHeights?.length,
    );
    return {
      start,
      end,
    };
  }
  calculateItemsInRange(scrollOffset: number, viewportSize: number): number[] {
    type Acc = { itemIndexesInRange: number[]; currentOffset: number };
    const visibleOffsetRange: Range = [
      scrollOffset,
      scrollOffset + viewportSize,
    ];
    return this.itemHeights?.reduce<Acc>(
      (acc, itemHeight, index) => {
        const itemOffsetRange: Range = [
          acc.currentOffset,
          acc.currentOffset + itemHeight,
        ];
        return {
          currentOffset: acc.currentOffset + itemHeight,
          itemIndexesInRange: intersects(itemOffsetRange, visibleOffsetRange)
            ? [...acc.itemIndexesInRange, index]
            : acc.itemIndexesInRange,
        };
      },
      { itemIndexesInRange: [], currentOffset: 0 },
    ).itemIndexesInRange;
  }
  checkDataValuesMatchInputValues(productsInRange: number[]) {
    productsInRange?.forEach(index => {
      const trueIndex = index - this.indexOffset;
      const item = this.items[trueIndex];
      if (item?.itemType === ItemTypes.product) {
        const castedItem = item as InventoryProductViewModel;
        if (this.isEditMode) {
          this.setInputValueIfDataDoesNotMatch(
            HotKeys.case + '-' + this.inventoryId + '-' + trueIndex,
            castedItem.displayConvFactor?.toString(),
          );
        } else {
          this.setInputValueIfDataDoesNotMatch(
            HotKeys.case + '-' + this.inventoryId + '-' + trueIndex,
            castedItem.caseOnHand.currentValue.toString(),
          );
          this.setInputValueIfDataDoesNotMatch(
            HotKeys.each + '-' + this.inventoryId + '-' + trueIndex,
            castedItem.eachOnHand.currentValue.toString(),
          );
        }
      }
    });
  }

  private setInputValueIfDataDoesNotMatch(id: string, itemValue: string) {
    const ionInputElement = document.getElementById(id);
    const inputElement: HTMLInputElement = ionInputElement?.firstChild as HTMLInputElement;
    const inputValue = inputElement?.value;
    if (
      inputValue &&
      inputValue !== itemValue &&
      inputElement !== document.activeElement
    ) {
      const newValue = itemValue === '0' ? '' : itemValue;
      ionInputElement.setAttribute('value', newValue);
    }
  }

  private updateRenderedRange() {
    if (!this.viewport) return;
    this.viewport.renderedRangeStream;
    const viewportSize = this.viewport.measureViewportSize('vertical');
    let scrollOffset = this.viewport.measureScrollOffset();
    const itemsInRange = this.calculateItemsInRange(scrollOffset, viewportSize);
    const newRange = this.getListRangeAt(itemsInRange);
    const oldRange = this.viewport?.getRenderedRange();
    if (newRange.start === oldRange.start && oldRange.end === newRange.end)
      return;

    this.viewport.setRenderedRange(newRange);
    let offset = this.getItemOffset(newRange.start);
    this.viewport.setRenderedContentOffset(offset);
    this.scrolledIndexChange$.next(newRange.start);
    this.checkDataValuesMatchInputValues(itemsInRange);
  }
  private updateTotalContentSize() {
    const contentSize = this.getTotalContentSize();
    this.viewport?.setTotalContentSize(contentSize);
  }
  onContentScrolled() {
    this.updateRenderedRange();
  }
  onDataLengthChanged() {
    this.updateTotalContentSize();
    this.updateRenderedRange();
  }
  onContentRendered() {}
  onRenderedOffsetChanged() {}
  scrollToIndex(index: number, behavior: ScrollBehavior) {
    this.viewport?.scrollToOffset(this.getItemOffset(index), behavior);
  }
}

function factory(dir: InventoryVirtualScrollDirective) {
  return dir.scrollStrategy;
}
// --- Inventory Directive ---

@Directive({
  selector: 'cdk-virtual-scroll-viewport[appInvVs]',
  providers: [
    {
      provide: VIRTUAL_SCROLL_STRATEGY,
      useFactory: factory,
      deps: [forwardRef(() => InventoryVirtualScrollDirective)],
    },
  ],
})
export class InventoryVirtualScrollDirective implements OnChanges {
  constructor(
    private elRef: ElementRef,
    private cd: ChangeDetectorRef,
    private platformService: PlatformService,
  ) {}
  @Input() itemHeights: ItemHeight = [];
  @Input() indexOffset = 0;
  @Input() bufferBefore? = 10;
  @Input() bufferAfter? = 10;
  @Input() items: InventoryItem[] = [];
  @Input() isEditMode = false;
  @Input() inventoryId = '';
  scrollStrategy: InventoryVirtualScrollStrategy = new InventoryVirtualScrollStrategy(
    this.itemHeights,
    this.indexOffset,
    this.bufferBefore,
    this.bufferAfter,
    this.items,
    this.platformService,
    this.isEditMode,
    this.inventoryId,
  );
  ngOnChanges(changes: SimpleChanges) {
    if (
      'itemHeights' in changes ||
      'indexOffset' in changes ||
      'bufferBefore' in changes ||
      'bufferAfter' in changes ||
      'items' in changes ||
      'inventoryId' in changes
    ) {
      this.scrollStrategy.updateItemHeights(
        this.itemHeights,
        this.indexOffset,
        this.bufferBefore,
        this.bufferAfter,
        this.items,
        this.isEditMode,
        this.inventoryId,
      );
      this.cd.detectChanges();
    }
  }
}
